export type OutcomeCategory =
  'revenue_gain' | 'roas_uplift' | 'cost_savings' | 'lost_revenue' | 'planning'

export type Severity = 'needs_attention' | 'opportunity' | 'informational' | 'actual' | 'other'

export type Confidence = 'high' | 'medium' | 'low' | (string & {})

export const FIXED_OUTCOME_ORDER = [
  'revenue_gain',
  'roas_uplift',
  'cost_savings',
  'lost_revenue',
  'planning',
] as const satisfies readonly OutcomeCategory[]

export const FIXED_SEVERITY_ORDER = [
  'needs_attention',
  'opportunity',
  'informational',
  'actual',
  'other',
] as const satisfies readonly Severity[]

/** Production-backed element discriminator published by BE-INS-03. */
export type InsightElementType = 'table' | 'bar_chart' | 'line_graph' | 'funnel'

/**
 * A definition keys its field as `key`, always — `field` / `name` / `id` / `column` never
 * occur, and `InsightConfig.clean()` rejects a definition without a `key` (contract §2.3).
 */
export interface InsightColumnDefinition {
  key: string
  type?: string
  label?: string
  /** Marks the server-side sort; the first column carrying it wins. */
  sort?: 'asc' | 'desc'
  frozen?: boolean
}

/** A table row, keyed by column `key` (contract §2.2). */
export type InsightTableRow = Record<string, unknown>

/**
 * The author's `visualization_config`, served verbatim. The keys below are the ones the
 * ChartSpec recipe reads (contract §2.4); the rest of the object is not enumerated, so it
 * stays open.
 */
export interface InsightBarChartConfig {
  metric?: string
  dimension?: string
  limit?: number
  title?: string
  chart_title?: string
  [key: string]: unknown
}

/** As `InsightBarChartConfig`, for the line recipe (contract §2.4). */
export interface InsightLineGraphConfig {
  x_field?: string
  y_field?: string
  title?: string
  chart_title?: string
  [key: string]: unknown
}

/** `{hidden_dimensions}` on a table element (contract §2.1). */
export interface InsightTableMetadata {
  hidden_dimensions?: string[]
  [key: string]: unknown
}

export interface InsightLinePoint extends Record<string, unknown> {
  x: unknown
  y: unknown
  is_current?: boolean
  label?: string
  efficiency?: number
  incremental_value?: number
}

export interface InsightLineSeries extends Record<string, unknown> {
  entity_id?: string | number
  entity_name?: string
  points: InsightLinePoint[]
}

interface InsightChartContentBase {
  /** The same object as the element's `metadata` — equal on 308 of 308 production charts. */
  config?: Record<string, unknown>
}

export interface InsightBarChartContent extends InsightChartContentBase {
  data: InsightTableRow[]
}

export interface InsightLineGraphContent extends InsightChartContentBase {
  data: InsightLineSeries[]
  default_entity_id?: string | number
}

/** A funnel carries no rows: it renders from `config` plus the sibling table element. */
export interface InsightFunnelContent extends InsightChartContentBase {
  data?: undefined
}

export type InsightChartContent =
  InsightBarChartContent | InsightLineGraphContent | InsightFunnelContent

/** The four fields whose shape does not vary with `element_type` (contract §2.1). */
interface InsightElementBase {
  /** Ordered column keys the client pins on first render; `[]` on chart elements. */
  frozen_columns: string[]
  /** Rows **as served**, after the config's `row_limit` / chart `limit`. */
  row_count: number
  /** 1-based render order — table(s) first, the visualization element last. */
  order: number
  /** Spans the full pre-pagination frame, not the visible page. `null` when unavailable. */
  totals: Record<string, unknown> | null
}

export interface InsightTableElement extends InsightElementBase {
  element_type: 'table'
  /** Non-null only on a table element of a tabbed config. */
  tab_key: string | null
  content: InsightTableRow[]
  column_definitions: InsightColumnDefinition[]
  metadata: InsightTableMetadata
}

export interface InsightBarChartElement extends InsightElementBase {
  element_type: 'bar_chart'
  tab_key: null
  content: InsightBarChartContent
  column_definitions: null
  metadata: InsightBarChartConfig
}

export interface InsightLineGraphElement extends InsightElementBase {
  element_type: 'line_graph'
  tab_key: null
  content: InsightLineGraphContent
  column_definitions: null
  metadata: InsightLineGraphConfig
}

export interface InsightFunnelElement extends InsightElementBase {
  element_type: 'funnel'
  tab_key: null
  content: InsightFunnelContent
  column_definitions: null
  metadata: Record<string, unknown>
}

/**
 * One rendered data block as served by BE-INS-03 — nine fields, always all nine, discriminated
 * on `element_type`. Source of truth: backend `external_contracts/insights/README.md` §2.
 *
 * Rows are found by discriminating, never by guessing: a table's rows are `content` itself,
 * a bar/line chart's are `content.data`, and a funnel has none (§2.2). Chart hints live in
 * `metadata` (§2.4).
 *
 * The union carries **no index signature on purpose**. The retired v4-draft aliases
 * `type` / `rows` / `chart` were never emitted — measured zero times across 3,212 production
 * elements — so reading one has to fail the build rather than yield `unknown` and render an
 * empty panel (ORP-20179).
 */
export type InsightElement =
  InsightTableElement | InsightBarChartElement | InsightLineGraphElement | InsightFunnelElement

export interface InsightTab {
  key: string
  label?: string
  element_keys?: string[]
  [key: string]: unknown
}

export type InsightStatus = 'seen' | 'clicked' | 'dismissed' | 'applied'

/** Backend-tolerant read model shared by consumption and workbench result surfaces. */
export interface InsightResult {
  id: number
  config_id?: number
  config_enum?: string
  /**
   * The insight config, served verbatim alongside the result. Only the fields the consumption
   * surfaces read are typed; `unwrap` passes the rest through untouched.
   */
  config?: { column_definitions?: InsightColumnDefinition[] }
  label?: string
  title?: string
  content?: string
  outcome_category: OutcomeCategory | (string & {})
  severity: Severity | (string & {})
  confidence?: Confidence
  impact_score?: number
  impact_format?: string
  impact_metric?: string
  impact_score_details?: Record<string, unknown>
  platform?: string
  platform_label?: string
  elements: InsightElement[]
  tabs?: InsightTab[]
  status?: InsightStatus | null
  schedule_id?: number
  template_context?: Record<string, unknown>
  llm_explanation?: unknown
  /** LLM-suggested next steps. Served by `InsightDetailSerializer`; see `service/insightGuidance`. */
  llm_actions?: unknown
  /** Risks live at `llm_metadata.postprocess.risks` — the backend nests them per LLM stage. */
  llm_metadata?: Record<string, unknown>
  created_at?: string
  updated_at?: string
  [key: string]: unknown
}

export type InsightListResponse =
  { results: InsightResult[]; count?: number; total?: number } | InsightResult[]
